import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('English');
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const languages = [
    { code: 'en', name: 'English', flag: '/assets/flags/us-flag.svg' },
    { code: 'jp', name: 'Japanese', flag: '/assets/flags/jp-flag.svg' },
    { code: 'vn', name: 'Vietnamese', flag: '/assets/flags/vn-flag.svg' }
  ];

  const handleLanguageChange = (language) => {
    setCurrentLanguage(language.name);
    setIsLanguageOpen(false);
  };

  return (
    <header className="bg-white">
      {/* Dải hồng nhạt (trên cùng) */}
      <div className="bg-pink-200 h-1"></div>

      {/* Dải xanh dương - Info Bar */}
      <div className={`bg-pink-600 text-white py-3 transition-all duration-300 ${isScrolled ? 'opacity-0 h-0 overflow-hidden' : 'opacity-100'}`}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between text-sm space-y-2 sm:space-y-0">
            <div className="flex items-center space-x-4 text-center sm:text-left">
              <span className="text-xs sm:text-sm">FOR ANY QUESTIONS ABOUT OUR SERVICES YOU CAN CALL US AT: +84 965 985 960</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="hidden sm:inline">Help Center</span>
              <div className="relative">
                <button 
                  onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                  className="flex items-center space-x-2 hover:bg-pink-700 px-2 py-1 rounded transition-colors"
                >
                  <img 
                    src={languages.find(lang => lang.name === currentLanguage)?.flag} 
                    alt="Flag" 
                    className="w-4 h-3"
                  />
                  <span className="hidden sm:inline">{currentLanguage}</span>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {/* Language Dropdown */}
                {isLanguageOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                    {languages.map((language) => (
                      <button
                        key={language.code}
                        onClick={() => handleLanguageChange(language)}
                        className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 text-left"
                      >
                        <img 
                          src={language.flag} 
                          alt={`${language.name} flag`} 
                          className="w-4 h-3"
                        />
                        <span>{language.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dải trắng - Main Navigation */}
      <div className={`bg-white shadow-sm transition-all duration-300 ${isScrolled ? 'fixed top-0 left-0 right-0 z-50 shadow-lg border-b-2 border-pink-600' : ''}`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="/assets/logo/LEMA_LOGO.png" 
                alt="LEMASX" 
                className="h-8 sm:h-12 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/services" className="relative text-black font-medium text-base group">
                <span className="relative z-10">Services</span>
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-pink-600 transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link to="/project" className="relative text-black font-medium text-base group">
                <span className="relative z-10">Project</span>
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-pink-600 transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link to="/blog" className="relative text-black font-medium text-base group">
                <span className="relative z-10">Blog</span>
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-pink-600 transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link to="/company" className="relative text-black font-medium text-base group">
                <span className="relative z-10">Company</span>
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-pink-600 transition-all duration-300 group-hover:w-full"></span>
              </Link>
            </nav>

            {/* CTA Button */}
            <div className="hidden md:flex items-center space-x-4">
              <Link
                to="/contact"
                className="bg-pink-600 text-white px-6 py-3 rounded-full hover:bg-pink-700 transition-colors font-medium"
              >
                Contact Us
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-md text-black hover:text-pink-600 hover:bg-gray-100"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <nav className="flex flex-col space-y-4">
                <Link to="/services" className="text-black hover:text-pink-600 transition-colors font-medium py-2">
                  Services
                </Link>
                <Link to="/project" className="text-black hover:text-pink-600 transition-colors font-medium py-2">
                  Project
                </Link>
                <Link to="/blog" className="text-black hover:text-pink-600 transition-colors font-medium py-2">
                  Blog
                </Link>
                <Link to="/company" className="text-black hover:text-pink-600 transition-colors font-medium py-2">
                  Company
                </Link>
                <Link
                  to="/quote"
                  className="bg-pink-600 text-white px-6 py-3 rounded-full hover:bg-pink-700 transition-colors font-medium text-center"
                >
                  GET INSTANT QUOTE
                </Link>
              </nav>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
